<template>
  <aside
    class="flex flex-col gap-2 justify-center h-full w-full md:w-[300px] rounded-r-2xl bg-white"
    data-test="navbar-employee-desktop-project"
  >
    <div class="h-[200px] flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <avatar
          :format="current_client.attributes.picture_logo_format"
          :src="current_client.attributes.picture_logo_url_s"
          :username="current_client.attributes.name"
          class="is-clickable"
          size="90"
        />
        <div class="flex gap-2 items-center">
          <div
            class="flex items-center gap-2 is-clickable"
            @click="togglePopover($event)"
          >
            <p class="text-center">{{ $t('navbar.topnavbar.selectTeam') }}</p>
            <FontAwesomeIcon
              :icon="['fas', 'chevron-down']"
              size="sm"
            />
          </div>
          <Popover ref="select_tenant_popover">
            <SelectTenant />
          </Popover>
        </div>
      </div>
    </div>

    <AppLink
      :class="{ 'font-bold has-text-primary text-shadow-2xs': route.name.startsWith(ROUTES_PARTNERS.CLIENT_DASHBOARD) }"
      :to="{ name: ROUTES_PARTNERS.CLIENT_DASHBOARD }"
      class="grid grid-cols-[30px_auto] gap-2 items-center px-5 text-sm"
      @click="emit('close')"
    >
      <div class="flex items-center justify-center">
        <FontAwesomeIcon
          :icon="['far', 'grid-horizontal']"
          size="lg"
        />
      </div>
      {{ $t('client.labels.dashboard') }}
    </AppLink>

    <div class="px-5 mb-3">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          :placeholder="$t('project.labels.searchProjects')"
          size="small"
        />
      </IconField>
    </div>

    <div class="flex flex-col gap-3 overflow-y-auto grow px-5">
      <NavbarProjectItem
        v-for="project in filteredProjects"
        :key="project.id"
        :is_active="activeProjectId === project.id"
        :project="project"
        @close="emit('close')"
        @toggle="handleProjectToggle"
      />
      <div
        v-if="filteredProjects.length === 0"
        class="text-sm text-gray-500"
      >
        {{ $t('project.labels.noProjects') }}
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-tooltip.bottom="$t('client.labels.contactZiggu')"
        class="cursor-pointer font-medium hover:bg-surface-100 rounded-md p-2 mx-4 mb-0 text-sm grid grid-cols-2 items-center gap-2 grid-cols-[20px_auto]"
        @click="openIntercom"
      >
        <FontAwesomeIcon
          :icon="['far', 'comment']"
          size="lg"
        />
        {{ $t('client.labels.chatWithUs') }}
      </div>
      <HelpSidebar />
    </div>

    <div class="p-3">
      <ProfileDropdown
        #reference
        :is_mobile="true"
        @close-sidebar="emit('close')"
      />
    </div>
  </aside>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';
import NavbarProjectItem from '@/app/_partners/components/navbar/NavbarProjectItem.vue';
import ProfileDropdown from '@/app/shared_components/navbar/TheProfileDropdown.vue';
import HelpSidebar from '@/app/shared_components/navbar/TheHelpSidebar.vue';
import { useRoute } from 'vue-router';
import SelectTenant from '@/app/shared_components/SelectTenant.vue';

const store = useStore();
const route = useRoute();

const emit = defineEmits(['close']);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);

const has_multiple_profiles = current_profile.value.attributes.profiles_count > 1;

const projects = ref([]);
const searchQuery = ref('');

const filteredProjects = computed(() => {
  if (!searchQuery.value.trim()) {
    return projects.value;
  }

  return _.filter(projects.value, (project) =>
    _.includes(project.attributes.name.toLowerCase(), searchQuery.value.trim().toLowerCase()),
  );
});

const activeProjectId = ref(null);

const handleProjectToggle = (projectId) => {
  if (activeProjectId.value === projectId) {
    activeProjectId.value = null;
  } else {
    activeProjectId.value = projectId;
  }
};

const select_tenant_popover = ref(null);

const togglePopover = (event) => {
  select_tenant_popover.value.toggle(event);
};

const openIntercom = () => {
  window.Intercom?.('show');
};

onMounted(async () => {
  projects.value = await store.dispatch('FETCH_PROJECTS');

  const urlProjectId = route.query.project_id || route.params.project_id;
  if (urlProjectId) {
    activeProjectId.value = urlProjectId;
  }
});
</script>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.198);
  border-radius: 3px;
  border: none;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.6);
}
</style>
